import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { AutoAwesomeOutlined } from "@mui/icons-material";
import { colors } from "../../theme/colors";
import { recordUiExperimentPromptShown } from "../../universal/uiExperiments";
import useUiDesignActions from "../app/useUiDesignActions";

const DECLINE_HINT_DISMISS_KEY = "ouro.uiV2DeclineHintDismissed.v1";

const BANNER_PAPER_SX = {
  mb: 1,
  p: 2,
  borderRadius: "16px",
  border: "1px solid rgba(255,255,255,0.08)",
  borderLeft: `4px solid ${colors.primary.main}`,
  background: `
    linear-gradient(
      180deg,
      rgba(255,255,255,0.03) 0%,
      rgba(246,185,76,0.05) 100%
    )
  `,
};

function readDeclineHintDismissed() {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return window.localStorage.getItem(DECLINE_HINT_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDeclineHintDismissed() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(DECLINE_HINT_DISMISS_KEY, "1");
  } catch {
    // Best-effort local dismiss only.
  }
}

function clearDeclineHintDismissed() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(DECLINE_HINT_DISMISS_KEY);
  } catch {
    // Best-effort local dismiss only.
  }
}

export default function V2DesignPrompt({ user, actions }) {
  const shownRef = useRef(false);
  const [hintDismissed, setHintDismissed] = useState(readDeclineHintDismissed);
  const {
    pending,
    isEligible,
    promptPending,
    acceptPrompt,
    declinePrompt,
  } = useUiDesignActions({ user, actions });

  const promptState = user?.uiV2PromptState;
  const preference = user?.uiDesignPreference;
  const showDeclineHint =
    isEligible &&
    promptState === "declined" &&
    preference !== "v2" &&
    !hintDismissed;

  useEffect(() => {
    if (!promptPending || shownRef.current) {
      return;
    }

    shownRef.current = true;
    recordUiExperimentPromptShown({ surface: "lobby" }).catch(() => {});
  }, [promptPending]);

  const handleDismissHint = () => {
    writeDeclineHintDismissed();
    setHintDismissed(true);
  };

  const handleDecline = async () => {
    clearDeclineHintDismissed();
    setHintDismissed(false);
    await declinePrompt();
  };

  if (promptPending) {
    return (
      <Paper variant="outlined" sx={BANNER_PAPER_SX}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AutoAwesomeOutlined sx={{ color: colors.primary.main, fontSize: "1.25rem" }} />
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: colors.text.primary }}>
                Try the new Ouro design
              </Typography>
            </Box>
            <Typography
              color="text.secondary"
              sx={{ mt: 0.35, ml: "calc(1.25rem + 8px)", fontSize: "0.85rem" }}
            >
              We refreshed the lobby and navigation. You can switch anytime from Account settings.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              color="warning"
              size="large"
              disabled={pending}
              onClick={acceptPrompt}
              sx={{ borderRadius: "12px", textTransform: "none" }}
            >
              Switch to new design
            </Button>
            <Button
              variant="text"
              color="warning"
              size="large"
              disabled={pending}
              onClick={handleDecline}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                color: "text.secondary",
                border: "0 !important",
                boxShadow: "none !important",
                backgroundColor: "transparent",
                minWidth: "auto",
                px: 0.75,
                "&:hover": {
                  color: colors.primary.main,
                  textDecoration: "underline",
                  border: "0 !important",
                  boxShadow: "none !important",
                  backgroundColor: "transparent",
                },
              }}
            >
              Not now
            </Button>
          </Box>
        </Box>
      </Paper>
    );
  }

  if (!showDeclineHint) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={BANNER_PAPER_SX}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeOutlined sx={{ color: colors.primary.main, fontSize: "1.25rem" }} />
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: colors.text.primary }}>
              New design available
            </Typography>
          </Box>
          <Typography
            color="text.secondary"
            sx={{ mt: 0.35, ml: "calc(1.25rem + 8px)", fontSize: "0.85rem" }}
          >
            You can switch to the new design anytime in{" "}
            <Box
              component={RouterLink}
              to="/account"
              sx={{
                color: colors.primary.main,
                fontWeight: 600,
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Account settings
            </Box>
            .
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <Button
            component={RouterLink}
            to="/account"
            variant="outlined"
            color="warning"
            size="large"
            sx={{ borderRadius: "12px", textTransform: "none" }}
          >
            Open Account settings
          </Button>
          <Button
            variant="text"
            color="warning"
            size="large"
            onClick={handleDismissHint}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              color: "text.secondary",
              border: "0 !important",
              boxShadow: "none !important",
              backgroundColor: "transparent",
              minWidth: "auto",
              px: 0.75,
              "&:hover": {
                color: colors.primary.main,
                textDecoration: "underline",
                border: "0 !important",
                boxShadow: "none !important",
                backgroundColor: "transparent",
              },
            }}
          >
            Dismiss
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

V2DesignPrompt.propTypes = {
  user: PropTypes.object,
  actions: PropTypes.object,
};
